// Location tracking for everyone who moves around the market — mounted by BOTH
// MobileShell (SE/ASM) and OfficeShell (ZSM/RSM/Sales Head, who also visit the
// market but land on the office layout and were never tracked at all).
//
//   * Dense trail (useLocationTrail): while punched in, ~1 fix/min moving,
//     5 min when stationary. Needs only READ_GEO_LOCATION (it used to also
//     require TA_DA, which left managers without it with no trail).
//   * Heartbeat (useLocationHeartbeat): one fix every 5 min whenever the app is
//     open and the dense trail isn't running — covers managers who visit the
//     market without punching in or logging any call/visit, so the Location Map
//     and its kilometres don't depend on call punches.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLocationTrail } from "@/offline/useLocationTrail";
import { useLocationHeartbeat } from "@/offline/useLocationHeartbeat";

const TRACKED_OFFICE_ROLES = ["ZSM", "RSM", "SALES_HEAD"];
const FIELD_ROLES = ["SE", "ASM"];

export function useFieldTracking(user) {
  const role = user?.role_code;
  const workflowPerms = user?.workflow_permissions || user?.workflow_perms || [];
  const hasReadGeo = workflowPerms.includes("READ_GEO_LOCATION");
  const tracked = !!role && (FIELD_ROLES.includes(role) || TRACKED_OFFICE_ROLES.includes(role));

  const [punchedIn, setPunchedIn] = useState(false);
  useEffect(() => {
    if (!tracked) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/attendance/today");
        if (cancelled) return;
        setPunchedIn(!!(r.data && r.data.check_in_ts && !r.data.check_out_ts));
      } catch { if (!cancelled) setPunchedIn(false); }
    };
    tick();
    // Battery: check punch status every 2 min (was 1) and only while the screen is visible.
    const t = setInterval(() => { if (document.visibilityState === "visible") tick(); }, 2 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [tracked]);

  const trail = useLocationTrail({
    enabled: tracked && punchedIn, hasTaDa: true, hasReadGeo,
  });
  const heartbeat = useLocationHeartbeat({
    enabled: tracked && hasReadGeo && !trail.active,
  });
  return { trail, heartbeat, punchedIn };
}
